import { format } from "date-fns";

import { PRE_REGISTRATION_REGION } from "@/app/(pre_release)/_shared/preRegistrationConstants";
import type {
  TrustScoreCancelSimulationStatus,
  TrustScoreGrade,
} from "@/gql/__generated__/graphql";

type WindowWithDataLayer = Window & {
  dataLayer: Record<string, unknown>[];
};

declare const window: WindowWithDataLayer;

export type PreRegistrationCompleteEventParams = {
  hasIdentification: boolean;
  hasBankAccount: boolean;
  identificationStatus: string;
  isFullyPrepared: boolean;
};

export const GTM_ID = process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID;

export const sendPreRegistrationCompleteEvent = (
  params: PreRegistrationCompleteEventParams,
) => {
  const payload = {
    event: "pre_registration_complete",
    region: PRE_REGISTRATION_REGION,
    has_identification: params.hasIdentification,
    has_bank_account: params.hasBankAccount,
    identification_status: params.identificationStatus,
    is_fully_prepared: params.isFullyPrepared,
  };

  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push(payload);
  } else {
    console.log(payload);
  }
};

/** 評価対象の実績なし（ランクを持たない）を示す値。現在・試算の内部値の位置に入れる */
export const NO_EVALUATION_RECORD_EVENT_GRADE = "no_record";

/**
 * 現在が評価対象の実績なしのときの変動表示の判定結果（D1 / D2 / D3）。
 * 試算の状態に接頭辞を付け、評価対象の実績がある場合（A3 / A4 / A5）と区別する
 */
export type NoEvaluationRecordCancelVariation =
  `no_record_${TrustScoreCancelSimulationStatus}`;

export type TrustScoreCancelEventParams = {
  /** 同じ確認画面の表示ごとに C14〜C16 を対応付けるための識別子 */
  viewId: string;
  /**
   * 信頼スコアの変動表示の判定結果（A3 / A4 / A5、現在が評価対象の実績なしのときは D1 / D2 / D3）。
   * 試算が未取得のときは simulation_pending、失敗したときは simulation_failed
   */
  variation:
    | TrustScoreCancelSimulationStatus
    | NoEvaluationRecordCancelVariation
    | "simulation_pending"
    | "simulation_failed";
  /** 現在の信頼スコア（内部値）。評価対象の実績なしのときは no_record、試算できなかったときは null */
  currentGrade:
    | TrustScoreGrade
    | typeof NO_EVALUATION_RECORD_EVENT_GRADE
    | null;
  /**
   * キャンセル後に試算した信頼スコア（内部値）。試算できなかったときは null。
   * 現在が評価対象の実績なしで変動を表示しない D1 のときは no_record
   */
  simulatedGrade:
    | TrustScoreGrade
    | typeof NO_EVALUATION_RECORD_EVENT_GRADE
    | null;
  /** 今回のキャンセルで直前キャンセルにあたる件数 */
  lateCancellationTargetCount: number;
  /** 連動求人をまとめてキャンセルするかどうか */
  bulkCancel: boolean;
};

// 計測の失敗で操作や画面表示を止めないため、送信は握りつぶす
const pushToDataLayer = (payload: Record<string, unknown>) => {
  try {
    if (typeof window.dataLayer !== "undefined") {
      window.dataLayer.push(payload);
    } else {
      console.log(payload);
    }
  } catch (error) {
    console.error(error);
  }
};

const buildTrustScoreCancelPayload = (
  event: string,
  params: TrustScoreCancelEventParams,
) => ({
  event,
  view_id: params.viewId,
  variation: params.variation,
  current_grade: params.currentGrade,
  simulated_grade: params.simulatedGrade,
  late_cancellation_target_count: params.lateCancellationTargetCount,
  bulk_cancel: params.bulkCancel,
});

/** キャンセル確認画面を表示したとき */
export const sendCancelConfirmViewEvent = (
  params: TrustScoreCancelEventParams,
) => {
  pushToDataLayer(buildTrustScoreCancelPayload("cancel_confirm_view", params));
};

/** キャンセルを確定したとき */
export const sendCancelConfirmSubmitEvent = (
  params: TrustScoreCancelEventParams,
) => {
  pushToDataLayer(
    buildTrustScoreCancelPayload("cancel_confirm_submit", params),
  );
};

/** キャンセルを確定せずに確認画面を閉じたとき */
export const sendCancelConfirmCloseEvent = (
  params: TrustScoreCancelEventParams,
) => {
  pushToDataLayer(buildTrustScoreCancelPayload("cancel_confirm_close", params));
};

/** 信頼スコアのヘルプを開いたとき */
export const sendTrustScoreHelpOpenEvent = () => {
  pushToDataLayer({ event: "trust_score_help_open" });
};

export const pageview = (url: string) => {
  if (typeof window.dataLayer !== "undefined") {
    // page_viewイベント送信
    window.dataLayer.push({
      event: "page_view",
      page: url,
    });

    // pageviewイベント送信
    window.dataLayer.push({
      event: "pageview",
      page: url,
    });
  } else {
    console.log({
      event: "page_view",
      page: url,
    });

    console.log({
      event: "pageview",
      page: url,
    });
  }
};

/** GTM の「GA4設定」タグが user_id として読む dataLayer のキー */
const ANALYTICS_USER_ID_KEY = "ga4_user_id";
/** GTM 側で user_id の設定・クリアを契機にタグを発火させたいときのイベント名 */
const ANALYTICS_USER_ID_EVENT = "ga4_user_id_update";

let currentAnalyticsUserId: string | null = null;

/** GTM が有効な環境か（GTM ID 未設定の開発環境などでは user_id を扱わない） */
export const isAnalyticsUserIdEnabled = (): boolean =>
  typeof window !== "undefined" &&
  Boolean(process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID);

/**
 * GA4 の user_id 用の値を dataLayer へ送る。
 * GTM が無効な環境では何もしない。失敗しても他の動作を妨げない。
 * @returns 送信できたとき true
 */
const pushAnalyticsUserId = (value: string | undefined): boolean => {
  if (!isAnalyticsUserIdEnabled()) return false;
  try {
    // GTM の読み込み前でも、GTM が読み込み時に dataLayer の内容を処理する
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: ANALYTICS_USER_ID_EVENT,
      [ANALYTICS_USER_ID_KEY]: value,
    });
    return true;
  } catch {
    return false;
  }
};

/** 分析専用の仮名ID（worker_id とは無関係）を GA4 の user_id に設定する */
export const setAnalyticsUserId = (analyticsId: string): boolean => {
  if (!pushAnalyticsUserId(analyticsId)) return false;
  currentAnalyticsUserId = analyticsId;
  return true;
};

/** 設定済みの user_id をクリアする（別のワーカーへ引き継がないため） */
export const clearAnalyticsUserId = (): void => {
  if (currentAnalyticsUserId === null) return;
  // dataLayer の値は undefined を送るとクリアされる
  if (pushAnalyticsUserId(undefined)) {
    currentAnalyticsUserId = null;
  }
};

export const hasAnalyticsUserId = (): boolean =>
  currentAnalyticsUserId !== null;

/**
 * Googleカレンダーに追加するためのURLを生成する
 * @param params カレンダーイベントのパラメータ
 * @returns GoogleカレンダーのURL
 */
export function generateGoogleCalendarUrl(params: {
  title: string;
  startDate: Date;
  endDate: Date;
  location: string;
  timezone?: string;
}): string {
  const formatDate = (date: Date) => format(date, "yyyyMMdd'T'HHmmss");
  const urlParams = new URLSearchParams({
    action: "TEMPLATE",
    text: params.title,
    dates: `${formatDate(params.startDate)}/${formatDate(params.endDate)}`,
    location: params.location,
    ctz: params.timezone || "Asia/Tokyo",
  });
  return `https://www.google.com/calendar/render?${urlParams.toString()}`;
}
